import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, filter, map, of, switchMap } from 'rxjs';
import { UserApiService } from '../../services/user-api.service';
import { UserInfoActions } from './user-info.actions';
import { getBrand } from '../../../utils/getBrand';
import { defaultFirstName, selectFirstName } from './user-info.reducer';
import { SafeSendActions } from '../safe-send/safe-send.actions';
import { SafeSendQuestions } from '@ucx-js/chat-ui';
import { TranslateService } from '@ngx-translate/core';
import { getAccountNumber } from '../../../utils/userProfile';
import { RealmCode } from '../../types/enum';
import { selectSafeSendState } from '../safe-send/safe-send.reducer';

@Injectable({
  providedIn: 'root',
})
export class UserInfoEffects {
  private bannedWords = {
    en: [],
    fr: [],
  };

  constructor(
    private actions$: Actions,
    private store: Store,
    private userApiService: UserApiService,
    private translateService: TranslateService
  ) {}

  getUserName = createEffect(() =>
    this.actions$.pipe(
      ofType(UserInfoActions.getUserInfo),
      concatLatestFrom(() => this.store.select(selectFirstName)),
      switchMap(([action, firstName]) => {
        if (!firstName || firstName === defaultFirstName) {
          return this.userApiService
            .getUserAccount(
              getAccountNumber(),
              this.translateService.currentLang
            )
            .pipe(
              map((result) =>
                UserInfoActions.getUserInfoSuccess({
                  userInfo: result.content,
                  nextStep: action.nextStep,
                })
              ),
              catchError((err) =>
                of(
                  action.onError(),
                  UserInfoActions.onGetUserAccountError({ error: err })
                )
              )
            );
        } else {
          return of(action.nextStep());
        }
      })
    )
  );

  ConnectToLiveChat = createEffect(() =>
    this.actions$.pipe(
      ofType(UserInfoActions.getUserInfoSuccess),
      map((action) => action.nextStep())
    )
  );

  signIn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserInfoActions.signIn),
      map(() => {
        const url =
          getBrand()?.code === RealmCode.rogers
            ? '/consumer/profile/signin'
            : '/profile/signin';
        window.parent.location.assign(window.parent.origin + url);
        return UserInfoActions.pendingSignIn();
      })
    )
  );

  getSafeSendNameAnswer = (
    safeSendArray: Array<{ question: string; answer: string }>,
    nameType: string
  ) => {
    return safeSendArray
      .find(
        (item) =>
          item.question.toLowerCase() ===
            SafeSendQuestions[`ENGLISH_${nameType}`].toLowerCase() ||
          item.question.toLowerCase() ===
            SafeSendQuestions[`FRENCH_${nameType}`].toLowerCase()
      )
      ?.answer?.trim();
  };

  setCustomerFirstAndLastName$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserInfoActions.validateCustomerName),
      concatLatestFrom(() => this.store.select(selectSafeSendState)),
      filter(([, safeSend]) => {
        const firstName = safeSend?.safesendInfoArray
          ? this.getSafeSendNameAnswer(safeSend.safesendInfoArray, 'FULL_NAME')
          : '';
        const isValidName =
          !this.bannedWords[this.translateService.currentLang]?.includes(
            firstName
          );
        return safeSend?.submitRequested && firstName && isValidName;
      }),
      map(([, safeSend]) =>
        UserInfoActions.setCustomerFirstAndLastName({
          firstName: safeSend?.safesendInfoArray
            ? this.getSafeSendNameAnswer(
                safeSend.safesendInfoArray,
                'FULL_NAME'
              )
            : '',
        })
      )
    )
  );

  setCustomerFirstName$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserInfoActions.validateCustomerName),
      concatLatestFrom(() => this.store.select(selectSafeSendState)),
      filter(([, safeSend]) => {
        const firstName = safeSend?.safesendInfoArray
          ? this.getSafeSendNameAnswer(safeSend.safesendInfoArray, 'FIRST_NAME')
          : '';
        const isValidName =
          !this.bannedWords[this.translateService.currentLang]?.includes(
            firstName
          );
        return safeSend?.submitRequested && firstName && isValidName;
      }),
      map(([, safeSend]) =>
        UserInfoActions.setCustomerFirstName({
          firstName: safeSend?.safesendInfoArray
            ? this.getSafeSendNameAnswer(
                safeSend.safesendInfoArray,
                'FIRST_NAME'
              )
            : '',
        })
      )
    )
  );

  setCustomerLastName$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserInfoActions.validateCustomerName),
      concatLatestFrom(() => this.store.select(selectSafeSendState)),
      filter(([, safeSend]) => {
        const lastName = safeSend?.safesendInfoArray
          ? this.getSafeSendNameAnswer(safeSend.safesendInfoArray, 'LAST_NAME')
          : '';
        const isValidName =
          !this.bannedWords[this.translateService.currentLang]?.includes(
            lastName
          );
        return safeSend?.submitRequested && lastName && isValidName;
      }),
      map(([, safeSend]) =>
        UserInfoActions.setCustomerLastName({
          lastName: safeSend?.safesendInfoArray
            ? this.getSafeSendNameAnswer(
                safeSend.safesendInfoArray,
                'LAST_NAME'
              )
            : '',
        })
      )
    )
  );

  loadBannedWords$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SafeSendActions.updateSafeSend,
        SafeSendActions.submitSuccessfully
      ),
      switchMap(() => {
        if (this.bannedWords[this.translateService.currentLang]?.length) {
          return of(UserInfoActions.validateCustomerName());
        }
        return this.userApiService
          .loadBannedWords(this.translateService.currentLang)
          .pipe(
            map((result: string[]) => {
              this.bannedWords[this.translateService.currentLang] = result;
              return UserInfoActions.validateCustomerName();
            })
          );
      })
    )
  );
}
